import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import CopyToClipboardButton from "@/components/shared/CopyToClipboardButton";

const GmailIntegration: React.FC<{
  dashboardUrl: string;
  projectDeveloper: string;
}> = ({ dashboardUrl, projectDeveloper }) => {
  const { t, language } = useLanguage();

  const projectDeveloperFolder =
    projectDeveloper === "Tree.ly, Dornbirn" ? "treely" : "wkss";

  let bannerUrl = "";
  const key = `${projectDeveloperFolder}_${language}`;

  switch (key) {
    case "treely_de":
      bannerUrl =
        "https://cdn.tree.ly/assets/v5/registry-app/sustainability_badge_treely_de.png";
      break;
    case "treely_en":
      bannerUrl =
        "https://cdn.tree.ly/assets/v5/registry-app/sustainability_badge_treely_en.png";
      break;
    case "wkss_de":
      bannerUrl =
        "https://cdn.tree.ly/assets/v5/registry-app/sustainability_badge_wkss_de.png";
      break;
    case "wkss_en":
      bannerUrl =
        "https://cdn.tree.ly/assets/v5/registry-app/sustainability_badge_wkss_en.png";
      break;
    default:
      // Default to Tree.ly German badge
      bannerUrl =
        "https://cdn.tree.ly/assets/v5/registry-app/sustainability_badge_treely_de.png";
  }

  // Helper function to render HTML content safely
  const renderHTML = (htmlString: string) => {
    return { __html: htmlString };
  };

  return (
    <div className="space-y-6">
      <ol className="list-none space-y-8 p-0 m-0">
        {/* Step 1 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            1
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.gmailIntegration.step1Title")}
          </h3>
          <p className="text-gray-700">
            <span
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.gmailIntegration.step1DescriptionPart1")
              )}
            />
            <span className="text-gray-700" style={{ fontStyle: "italic" }}>
              {" "}
              (https://mail.google.com)
            </span>
            <CopyToClipboardButton text="https://mail.google.com" />{" "}
            <span
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.gmailIntegration.step1DescriptionPart2")
              )}
            />
          </p>
        </li>

        {/* Step 2 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            2
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.gmailIntegration.step2Title")}
          </h3>
          <p
            className="text-gray-700 mb-3"
            dangerouslySetInnerHTML={renderHTML(
              t("integrationGuide.gmailIntegration.step2Description")
            )}
          />
          <div className="-ml-2.5">
            <img
              src={`/integrations/gmailIntegration/${language}/step2.png`}
              alt="Step 3"
              className="w-full h-full object-contain"
            />
          </div>
        </li>

        {/* Step 3 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            3
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.gmailIntegration.step3Title")}
          </h3>
          <ul className="list-disc pl-5 mb-3 space-y-1 text-gray-700">
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.gmailIntegration.step3DescriptionPoint1")
              )}
            />
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.gmailIntegration.step3DescriptionPoint2")
              )}
            />
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.gmailIntegration.step3DescriptionPoint3")
              )}
            />
          </ul>
          <div className="-ml-2.5">
            <img
              src={`/integrations/gmailIntegration/${language}/step3.png`}
              alt="Step 4"
              className="w-full h-full object-contain"
            />
          </div>
        </li>

        {/* Step 4 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            4
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.gmailIntegration.step4Title")}
          </h3>
          <ul className="list-disc pl-5 mb-3 space-y-1 text-gray-700">
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.gmailIntegration.step4DescriptionPoint1")
              )}
            />
            <li>
              <span
                dangerouslySetInnerHTML={renderHTML(
                  t(
                    "integrationGuide.gmailIntegration.step4DescriptionPoint2",
                    {
                      imageUrl: `<span style="font-style: italic">${bannerUrl}</span>`,
                    }
                  )
                )}
              />
              <CopyToClipboardButton text={dashboardUrl} />
            </li>
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.gmailIntegration.step4DescriptionPoint3")
              )}
            />
          </ul>
          <div className="-ml-2.5">
            <img
              src={`/integrations/gmailIntegration/${language}/step4.png`}
              alt="Step 5"
              className="w-full h-full object-contain"
            />
          </div>
        </li>

        {/* Step 5 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            5
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.gmailIntegration.step5Title")}
          </h3>
          <ul className="list-disc pl-5 mb-3 space-y-1 text-gray-700">
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.gmailIntegration.step5DescriptionPoint1")
              )}
            />
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.gmailIntegration.step5DescriptionPoint2")
              )}
            />
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.gmailIntegration.step5DescriptionPoint3")
              )}
            />
            <span className="text-gray-700" style={{ fontStyle: "italic" }}>
              {" "}
              {dashboardUrl}
            </span>
            <CopyToClipboardButton text={dashboardUrl} />
            <li
              dangerouslySetInnerHTML={renderHTML(
                t("integrationGuide.gmailIntegration.step5DescriptionPoint4")
              )}
            />
          </ul>
          <div className="-ml-2.5">
            <img
              src={`/integrations/gmailIntegration/${language}/step5.png`}
              alt="Step 6"
              className="w-full h-full object-contain"
            />
          </div>
        </li>

        {/* Step 6 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            6
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.gmailIntegration.step6Title")}
          </h3>
          <p
            className="text-gray-700 mb-3"
            dangerouslySetInnerHTML={renderHTML(
              t("integrationGuide.gmailIntegration.step6Description")
            )}
          />
          <div className="-ml-2.5">
            <img
              src={`/integrations/gmailIntegration/${language}/step6.png`}
              alt="Step 7"
              className="w-full h-full object-contain"
            />
          </div>
        </li>

        {/* Step 7 */}
        <li className="relative pl-10">
          <div className="absolute left-0 top-0 flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-700 font-semibold">
            7
          </div>
          <h3 className="text-base font-semibold mb-2">
            {t("integrationGuide.gmailIntegration.step7Title")}
          </h3>
          <p
            className="text-gray-700"
            dangerouslySetInnerHTML={renderHTML(
              t("integrationGuide.gmailIntegration.step7Description")
            )}
          />
        </li>
      </ol>
    </div>
  );
};

export default GmailIntegration;
